import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  Divider,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
  Stack,
  Badge,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeviceHub from "@mui/icons-material/DeviceHub";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";

import {
  Dashboard,
  Storage,
  SystemUpdate,
  MoreVert,
  Logout,
  People,
  Security,
  Memory,
  Place,
  Gavel,
  AccountCircle,
  Apartment,
  Report,
  SwapHoriz,
  Link,
  LocalShipping,
  Handshake,
  ReportProblem,
  Inventory2,
  Engineering,
  AssignmentTurnedIn,
  CurrencyRupee,
  FactCheck,
} from "@mui/icons-material";
import WebhookRoundedIcon from "@mui/icons-material/WebhookRounded";

import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import { can } from "../../auth/can";
import OrgSwitcher from "../OrgSwitcher";
import { useSidebar } from "../../auth/SidebarContext";

import { APP_INVENTORY_API_BASE } from "../../config/api";
const API_BASE = `${APP_INVENTORY_API_BASE}`;

const SIDEBAR_EXPANDED_WIDTH = 290;
const SIDEBAR_COLLAPSED_WIDTH = 72;

// ── Menu definitions ──────────────────────────────────────────────────────────

const primaryMenu = [
  {
    text: "Overview",
    icon: <Dashboard />,
    path: "/overview",
    perm: "organization.view",
  },
  {
    text: "Pricing",
    icon: <CurrencyRupee />,
    path: "/pricing",
    perm: "organization.view",
  },
];

const adminMenu = [
  {
    text: "Organizations",
    icon: <Apartment />,
    path: "/admin/organizations",
    perm: "organization.view",
  },
  {
    text: "Users",
    icon: <People />,
    path: "/admin/users",
    perm: "org.manage_users",
  },
  {
    text:"Attendance",
    icon:<FactCheck/>,
    path:"/admin/attendance",
    perm:"attendance.view"
  },
  {
    text: "Roles & Permissions",
    icon: <Security />,
    path: "/admin/roles",
    perm: "org.manage_roles",
  },
];

const reportsMenu = [
  {
    text: "Reports",
    icon: <Report />,
    path: "/reports",
    perm: "reports.view",
  },
];

const integrationsMenu = [
  {
    text: "Webhooks",
    icon: <WebhookRoundedIcon />,
    path: "/integrations/webhooks",
    perm: "webhook.view",
  },
];

const commonServiceMenu = [
  {
    text: "oneM2M Console",
    icon: <HubRoundedIcon />,
    path: "/common-service/onem2m",
    perm: "device.view",
  },
];

const deviceManagementMenu = [
  {
    text: "Devices",
    icon: <DeviceHub />,
    path: "/device_onboarding",
    perm: "device.view",
  },
  {
    text: "Firmware",
    icon: <Storage />,
    path: "/ota/packages",
    perm: "system.manage",
  },
  {
    text: "Deployments",
    icon: <SystemUpdate />,
    path: "/ota/deployments",
    perm: "system.manage",
  },
  {
    text: "Device Types",
    icon: <Memory />,
    path: "/device_onboarding/devicetypes",
    perm: "system.manage",
  },
];

// ── Shared sx factories ───────────────────────────────────────────────────────

const getSectionButtonSx = (collapsed) => ({
  minHeight: 46,
  px: collapsed ? 0 : 1.25,
  justifyContent: collapsed ? "center" : "flex-start",
  borderRadius: 3,
  color: "#CBD5E1",
  "& .MuiListItemIcon-root": {
    minWidth: collapsed ? "auto" : 36,
    color: "inherit",
    justifyContent: "center",
  },
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.08)",
  },
});

const getChildButtonSx = (collapsed) => ({
  minHeight: 42,
  pl: collapsed ? 0 : 6,
  pr: collapsed ? 0 : 1.25,
  justifyContent: collapsed ? "center" : "flex-start",
  borderRadius: 3,
  color: "#94A3B8",
  "& .MuiListItemIcon-root": {
    minWidth: collapsed ? "auto" : 34,
    color: "inherit",
    justifyContent: "center",
  },
  "&.active": {
    color: "#E0F2FE",
    background:
      "linear-gradient(90deg, rgba(14,165,233,0.22), rgba(20,184,166,0.14))",
  },
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.06)",
  },
});

const getDirectLinkSx = (collapsed) => ({
  ...getSectionButtonSx(collapsed),
  "&.active": {
    color: "#F8FAFC",
    background:
      "linear-gradient(90deg, rgba(14,165,233,0.24), rgba(20,184,166,0.16))",
    boxShadow: "inset 0 0 0 1px rgba(103,232,249,0.12)",
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(user) {
  if (!user?.name) return "TC";
  return user.name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Sidebar() {
  const { logout, permissions, user, activeOrg } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const token = localStorage.getItem("authToken");
  const isSuperAdmin = user?.global_role === "superadmin";
  const activeOrgId = activeOrg?.id || "";

  // ── Sidebar collapsed state — now from shared context ──────────────────────
  const { collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed } =
    useSidebar();

  // ── Drawer open state (mobile) ──────────────────────────────────────────────
  const [open, setOpen] = useState(false);

  // ── Section expand states ───────────────────────────────────────────────────
  const [adminOpen, setAdminOpen] = useState(false);
  const [deviceManagementOpen, setDeviceManagementOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [manufacturingOpen, setManufacturingOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [commonServiceOpen, setCommonServiceOpen] = useState(false);

  // ── Profile menu ────────────────────────────────────────────────────────────
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const profileMenuOpen = Boolean(profileAnchorEl);

  // ── Create account modal ────────────────────────────────────────────────────
  const [openCreateModal, setOpenCreateModal] = useState(false);

  // ── RMA badge ───────────────────────────────────────────────────────────────
  const [openRmaCount, setOpenRmaCount] = useState(0);
  const lastRmaBadgeFetchKeyRef = useRef("");

  // When sidebar collapses, close all expandable sections
  useEffect(() => {
    if (sidebarCollapsed) {
      setAdminOpen(false);
      setDeviceManagementOpen(false);
      setInventoryOpen(false);
      setManufacturingOpen(false);
      setProjectsOpen(false);
      setIntegrationsOpen(false);
      setCommonServiceOpen(false);
    }
  }, [sidebarCollapsed]);

  const fetchOpenRmaCount = useCallback(async ({ force = false } = {}) => {
    if (!token) return;
    const fetchKey = `${token}:${activeOrgId}`;
    if (!force && lastRmaBadgeFetchKeyRef.current === fetchKey) return;
    lastRmaBadgeFetchKeyRef.current = fetchKey;

    const headers = {
      Authorization: `Bearer ${token}`,
      ...(activeOrgId ? { "X-Org-ID": activeOrgId } : {}),
    };

    try {
      const [res1, res2] = await Promise.all([
        fetch(`${API_BASE}/rma?case_status=IN_PROGRESS&per_page=1&page=1`, {
          headers,
        }),
        fetch(`${API_BASE}/rma?case_status=OPEN&per_page=1&page=1`, {
          headers,
        }),
      ]);

      const data1 = res1.ok ? await res1.json() : {};
      const data2 = res2.ok ? await res2.json() : {};

      const total =
        (data1?.meta?.pagination?.total_items || 0) +
        (data2?.meta?.pagination?.total_items || 0);

      setOpenRmaCount(total);
    } catch {
      // silently fail — badge shows 0
    }
  }, [token, activeOrgId]);

  useEffect(() => {
    fetchOpenRmaCount();
  }, [fetchOpenRmaCount]);

  // ── Auto-expand sections based on active route ──────────────────────────────
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) setAdminOpen(true);
    if (
      location.pathname.startsWith("/device_onboarding") ||
      location.pathname.startsWith("/ota") ||
      location.pathname.startsWith("/health")
    ) {
      setDeviceManagementOpen(true);
    }
    if (location.pathname.startsWith("/inventory")) setInventoryOpen(true);
    if (location.pathname.startsWith("/manufacturing")) setManufacturingOpen(true);
    if (location.pathname.startsWith("/projects")) setProjectsOpen(true);
    if (location.pathname.startsWith("/integrations")) setIntegrationsOpen(true);
    if (location.pathname.startsWith("/common-service")) setCommonServiceOpen(true);
  }, [location.pathname]);

  // ── Inventory menu ──────────────────────────────────────────────────────────
  const inventoryMenu = useMemo(
    () => [
      {
        text: "Overview",
        icon: <Memory />,
        path: "/inventory/overview",
        perm: "inventory.asset.view",
      },
      {
        text: "Assets",
        icon: <Memory />,
        path: "/inventory/assets",
        perm: "inventory.asset.view",
      },
      {
        text: "Transfers",
        icon: <SwapHoriz />,
        path: "/inventory/transfers",
        perm: "inventory.transfer.view",
      },
      {
        text: "Bindings",
        icon: <Link />,
        path: "/inventory/bindings",
        perm: "inventory.binding.view",
      },
      {
        text: "Movements",
        icon: <LocalShipping />,
        path: "/inventory/movements",
        perm: "inventory.movement.view",
      },
      {
        text: "Locations",
        icon: <Place />,
        path: "/inventory/locations",
        perm: "inventory.location.view",
      },
      {
        text: "Leases",
        icon: <Handshake />,
        path: "/inventory/leases",
        perm: "inventory.lease.view",
      },
      {
        text: "RMA Cases",
        icon: (
          <Badge
            badgeContent={openRmaCount}
            color="error"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 10,
                height: 16,
                minWidth: 16,
                padding: "0 4px",
              },
            }}
          >
            <ReportProblem />
          </Badge>
        ),
        path: "/inventory/rma",
        perm: "inventory.rma.view",
      },
    ],
    [openRmaCount],
  );

  const manufacturingMenu = useMemo(
    () => [
      {
        text: "Overview",
        icon: <PrecisionManufacturingIcon />,
        path: "/manufacturing/overview",
        perm: "inventory.asset.view",
      },
      {
        text: "Partners",
        icon: <Handshake />,
        path: "/manufacturing/partners",
        perm: "inventory.asset.view",
      },
      {
        text: "Products",
        icon: <Engineering />,
        path: "/manufacturing/products",
        perm: "inventory.asset.view",
      },
      {
        text: "Orders",
        icon: <Inventory2 />,
        path: "/manufacturing/orders",
        perm: "inventory.asset.view",
      },
    ],
    [],
  );

  const projectsMenu = useMemo(
    () => [
      {
        text: "Projects Dashboard",
        icon: <WorkspacesRoundedIcon />,
        path: "/projects/overview",
        perm: "inventory.asset.view",
      },
      {
        text: "Customers",
        icon: <Apartment />,
        path: "/projects/customers",
        perm: "inventory.asset.view",
      },
      {
        text: "Projects List",
        icon: <Gavel />,
        path: "/projects/deployments",
        perm: "inventory.asset.view",
      },
      {
        text: "Sites",
        icon: <Place />,
        path: "/projects/sites",
        perm: "inventory.asset.view",
      },
      {
        text: "Site Surveys",
        icon: <AssignmentTurnedIn />,
        path: "/projects/surveys",
        perm: "inventory.asset.view",
      },
    ],
    [],
  );

  // ── Permission-filtered menus ───────────────────────────────────────────────
  const visiblePrimaryMenu = useMemo(
    () => primaryMenu.filter((i) => can(permissions, i.perm)),
    [permissions],
  );
  const visibleAdminMenu = useMemo(
    () => adminMenu.filter((i) => can(permissions, i.perm)),
    [permissions],
  );
  const visibleDeviceManagementMenu = useMemo(
    () => deviceManagementMenu.filter((i) => can(permissions, i.perm)),
    [permissions],
  );
  const visibleReportsMenu = useMemo(
    () => reportsMenu.filter((i) => can(permissions, i.perm)),
    [permissions],
  );
  const visibleInventoryMenu = useMemo(
    () => inventoryMenu.filter((i) => can(permissions, i.perm)),
    [inventoryMenu, permissions],
  );
  const visibleManufacturingMenu = useMemo(
    () => manufacturingMenu.filter((i) => can(permissions, i.perm)),
    [manufacturingMenu, permissions],
  );
  const visibleProjectsMenu = useMemo(
    () =>
      isSuperAdmin
        ? projectsMenu.filter((i) => can(permissions, i.perm))
        : [],
    [isSuperAdmin, projectsMenu, permissions],
  );
  const visibleIntegrationsMenu = useMemo(
    () => integrationsMenu.filter((i) => can(permissions, i.perm)),
    [permissions],
  );
  const visibleCommonServiceMenu = useMemo(
    () => commonServiceMenu.filter((i) => can(permissions, i.perm)),
    [permissions],
  );

  // collapsed is always false on mobile (mobile uses drawer)
  const collapsed = !isMobile && sidebarCollapsed;

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderDirectLink = (item) => (
    <Tooltip
      key={item.text}
      title={collapsed ? item.text : ""}
      placement="right"
      arrow
    >
      <ListItemButton
        component={NavLink}
        to={item.path}
        onClick={() => isMobile && setOpen(false)}
        sx={getDirectLinkSx(collapsed)}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 700 }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );

  const renderExpandableSection = ({
    label,
    icon,
    openState,
    onToggle,
    items,
    headerBadge,
  }) => {
    if (!items.length) return null;

    if (collapsed) {
      return (
        <Box key={label}>
          {items.map((item) => (
            <Tooltip key={item.text} title={item.text} placement="right" arrow>
              <ListItemButton
                component={NavLink}
                to={item.path}
                sx={{
                  ...getChildButtonSx(true),
                  pl: 0,
                  mb: 0.5,
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
              </ListItemButton>
            </Tooltip>
          ))}
        </Box>
      );
    }

    return (
      <Box key={label}>
        <ListItemButton onClick={onToggle} sx={getSectionButtonSx(false)}>
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 700 }}
          />
          {headerBadge}
          {openState ? <ExpandMoreRoundedIcon /> : <ChevronRightRoundedIcon />}
        </ListItemButton>

        {openState && (
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {items.map((item) => (
              <ListItemButton
  key={item.text}
  component={NavLink}
  to={item.path}
  end
  onClick={() => {
    if (isMobile) setOpen(false);
  }}
  sx={getChildButtonSx(false)}
>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }}
                />
              </ListItemButton>
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  // ── Sidebar content ─────────────────────────────────────────────────────────

  const sidebarContent = (
    <Box
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        color: "#F8FAFC",
        background:
          "linear-gradient(180deg, #020617 0%, #0F172A 16%, #0B1F33 100%)",
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
        overflow: "hidden",
      }}
    >
      {isMobile && <Toolbar />}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: collapsed ? 1 : 2,
          pt: isMobile ? 2 : 2.5,
          pb: 1.5,
          borderBottom: "1px solid rgba(148, 163, 184, 0.10)",
          transition: "padding 0.25s",
          position: "relative",
        }}
      >
        {collapsed ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 0.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: "linear-gradient(135deg, #0EA5E9, #14B8A6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                fontSize: 15,
                color: "#fff",
                letterSpacing: "-0.03em",
                flexShrink: 0,
              }}
            >
              TC
            </Box>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#4ADE80",
                boxShadow: "0 0 6px #4ADE80",
              }}
            />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                component="img"
                src="/Coredata_Logo_black.svg" // 👉 replace with your image path
                alt="TrackCoreData Logo"
                sx={{
                  objectFit: "contain",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
                onClick={()=> navigate("/overview")}
              />
              <Box
                sx={{
                  width: "100%", // 👈 important
                  height: "1px", // 👈 important
                  backgroundColor: "rgba(255,255,255,0.3)", // visible on dark bg
                  // my: 1,
                }}
              />
              <Typography
                sx={{
                  fontSize: 25,
                  fontWeight: 400,
                  letterSpacing: "-0.04em",
                  color: "#F8FAFC",
                  whiteSpace: "nowrap",
                  fontFamily: "fangsong",
                }}
              >
                TrackCoreData
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 4,
                background:
                  "linear-gradient(135deg, rgba(14,165,233,0.14), rgba(20,184,166,0.10))",
                border: "1px solid rgba(103, 232, 249, 0.10)",
              }}
            >
              <Stack spacing={1.25}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    sx={{ fontSize: 12, color: "#BAE6FD", fontWeight: 700 }}
                  >
                    Active Organization
                  </Typography>
                  <Chip
                    size="small"
                    label="Live"
                    sx={{
                      height: 22,
                      color: "#DCFCE7",
                      backgroundColor: "rgba(34,197,94,0.12)",
                      border: "1px solid rgba(74,222,128,0.16)",
                    }}
                  />
                </Stack>
                <Typography
                  sx={{ fontSize: 16, fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {activeOrg?.name || "Select organization"}
                </Typography>
                <OrgSwitcher />
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>

      {/* ── Nav ────────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: collapsed ? 1 : 1.5,
          py: 2,
          transition: "padding 0.25s",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "#334155",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": { background: "#475569" },
          scrollbarWidth: "thin",
          scrollbarColor: "#334155 transparent",
        }}
      >
        <Stack spacing={2}>
          {/* Home */}
          <Box>
            {!collapsed && (
              <Typography
                sx={{
                  px: 1.25,
                  mb: 1,
                  color: "#64748B",
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  whiteSpace: "nowrap",
                }}
              >
                Home
              </Typography>
            )}
            {collapsed && (
              <Box
                sx={{
                  height: 1,
                  backgroundColor: "rgba(148,163,184,0.10)",
                  mx: 1,
                  mb: 1,
                }}
              />
            )}
            <List sx={{ p: 0 }}>
              <Stack spacing={0.75}>
                {visiblePrimaryMenu.map(renderDirectLink)}
              </Stack>
            </List>
          </Box>

          {/* Modules */}
          <Box>
            {!collapsed && (
              <Typography
                sx={{
                  px: 1.25,
                  mb: 1,
                  color: "#64748B",
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  whiteSpace: "nowrap",
                }}
              >
                Modules
              </Typography>
            )}
            {collapsed && (
              <Box
                sx={{
                  height: 1,
                  backgroundColor: "rgba(148,163,184,0.10)",
                  mx: 1,
                  mb: 1,
                }}
              />
            )}
            <List sx={{ p: 0 }}>
              <Stack spacing={0.75}>
                {renderExpandableSection({
                  label: "Device Management",
                  icon: <DeviceHub />,
                  openState: deviceManagementOpen,
                  onToggle: () => setDeviceManagementOpen((p) => !p),
                  items: visibleDeviceManagementMenu,
                })}
                {renderExpandableSection({
                  label: "Common Service",
                  icon: <HubRoundedIcon />,
                  openState: commonServiceOpen,
                  onToggle: () => setCommonServiceOpen((p) => !p),
                  items: visibleCommonServiceMenu,
                })}
                {visibleReportsMenu.map(renderDirectLink)}
                {renderExpandableSection({
                  label: "Projects",
                  icon: <WorkspacesRoundedIcon />,
                  openState: projectsOpen,
                  onToggle: () => setProjectsOpen((p) => !p),
                  items: visibleProjectsMenu,
                })}
                {renderExpandableSection({
                  label: "Inventory",
                  icon: <Storage />,
                  openState: inventoryOpen,
                  onToggle: () => setInventoryOpen((p) => !p),
                  items: visibleInventoryMenu,
                  headerBadge:
                    !inventoryOpen && openRmaCount > 0 ? (
                      <Badge
                        badgeContent={openRmaCount}
                        color="error"
                        max={99}
                        sx={{
                          mr: 1,
                          "& .MuiBadge-badge": {
                            fontSize: 10,
                            height: 16,
                            minWidth: 16,
                          },
                        }}
                      />
                    ) : null,
                })}
                {renderExpandableSection({
                  label: "Manufacturing",
                  icon: <PrecisionManufacturingIcon />,
                  openState: manufacturingOpen,
                  onToggle: () => setManufacturingOpen((p) => !p),
                  items: visibleManufacturingMenu,
                })}
                {visibleIntegrationsMenu.length > 0
                  ? renderExpandableSection({
                      label: "Integrations",
                      icon: <WebhookRoundedIcon />,
                      openState: integrationsOpen,
                      onToggle: () => setIntegrationsOpen((p) => !p),
                      items: visibleIntegrationsMenu,
                    })
                  : null}
                {renderExpandableSection({
                  label: "Administration",
                  icon: <People />,
                  openState: adminOpen,
                  onToggle: () => setAdminOpen((p) => !p),
                  items: visibleAdminMenu,
                })}
              </Stack>
            </List>
          </Box>
        </Stack>
      </Box>

      {/* ── User section ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          p: collapsed ? 1 : 1.5,
          borderTop: "1px solid rgba(148, 163, 184, 0.10)",
          backgroundColor: "rgba(2, 6, 23, 0.55)",
          transition: "padding 0.25s",
        }}
      >
        {collapsed ? (
          <Tooltip title={user?.name || "Admin User"} placement="right" arrow>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={(e) => setProfileAnchorEl(e.currentTarget)}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  fontWeight: 800,
                  color: "#082F49",
                  background: "linear-gradient(135deg, #BAE6FD, #67E8F9)",
                }}
              >
                {getInitials(user)}
              </Avatar>
            </Box>
          </Tooltip>
        ) : (
          <Box
            sx={{
              p: 1.25,
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(15,23,42,0.88), rgba(15,118,110,0.14))",
              border: "1px solid rgba(148, 163, 184, 0.10)",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Avatar
              sx={{
                width: 44,
                height: 44,
                fontWeight: 800,
                color: "#082F49",
                background: "linear-gradient(135deg, #BAE6FD, #67E8F9)",
                flexShrink: 0,
              }}
            >
              {getInitials(user)}
            </Avatar>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#F8FAFC",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.name || "Admin User"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: "#94A3B8",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.email || "No email"}
              </Typography>
            </Box>

            <IconButton
              onClick={(e) => setProfileAnchorEl(e.currentTarget)}
              sx={{ color: "#CBD5E1" }}
            >
              <MoreVert />
            </IconButton>
          </Box>
        )}

        {/* Profile Menu */}
        <Menu
          anchorEl={profileAnchorEl}
          open={profileMenuOpen}
          onClose={() => setProfileAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          PaperProps={{
            sx: {
              width: 250,
              borderRadius: 3,
              overflow: "hidden",

              // 🔥 Gradient background
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

              color: "#fff", // 👈 important for text visibility
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
              backdropFilter: "blur(10px)", // optional glass effect
            },
          }}
        >
          <Box sx={{ p: 2, display: "flex", gap: 1.5 }}>
            <Avatar sx={{ bgcolor: "#0EA5E9" }}>
              {user?.name?.[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography fontWeight={700}>{user?.name}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {user?.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

          <List dense disablePadding>
            <ListItemButton
              onClick={() => {
                setProfileAnchorEl(null);
                navigate("/profile");
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#fff" }}>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="My Profile" />
            </ListItemButton>

            <ListItemButton
              onClick={() => {
                setProfileAnchorEl(null);
                navigate("/profile/security");
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#fff" }}>
                <Security fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Security" />
            </ListItemButton>
          </List>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

          <List dense disablePadding>
            <ListItemButton
              onClick={logout}
              sx={{
                color: "#F87171",
                "&:hover": {
                  backgroundColor: "rgba(248,113,113,0.1)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#F87171" }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </List>
        </Menu>
      </Box>

      {/* ── Collapse toggle button ──────────────────────────────────────────── */}
      {!isMobile && (
        <Tooltip
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          placement="right"
          arrow
        >
          <IconButton
            onClick={() => setSidebarCollapsed((p) => !p)}
            size="small"
            sx={{
              position: "absolute",
              bottom: 80,
              right: collapsed ? "50%" : 12,
              transform: collapsed ? "translateX(50%)" : "none",
              transition: "right 0.25s, transform 0.25s",
              zIndex: 10,
              width: 28,
              height: 28,
              color: "#94A3B8",
              backgroundColor: "rgba(15,23,42,0.9)",
              border: "1px solid rgba(148,163,184,0.20)",
              "&:hover": {
                backgroundColor: "rgba(14,165,233,0.18)",
                color: "#E0F2FE",
                borderColor: "rgba(103,232,249,0.30)",
              },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      )}

      {/* ── Create Account Dialog ───────────────────────────────────────────── */}
      <Dialog
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Create Account</DialogTitle>
        <DialogContent>
          <TextField label="Full Name" fullWidth margin="normal" />
          <TextField label="Email" type="email" fullWidth margin="normal" />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
          />
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenCreateModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setOpenCreateModal(false)}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ── Layout wrapper ──────────────────────────────────────────────────────────

  const desktopWidth = collapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;

  return (
    <>
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            backdropFilter: "blur(16px)",
            color: "#fff",
            borderBottom: "1px solid rgba(226,232,240,0.9)",
          }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={() => setOpen((prev) => !prev)}>
              {open ? (
                <CloseIcon sx={{ color: "#fff" }} />
              ) : (
                <MenuIcon sx={{ color: "#fff" }} />
              )}
            </IconButton>
            <Typography
              sx={{ ml: 1, fontWeight: 800, letterSpacing: "-0.03em" }}
            >
              TrackCoreData
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {!isMobile && (
        <Box
          sx={{
            width: desktopWidth,
            height: "100vh",
            position: "fixed",
            left: 0,
            transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
            zIndex: 1200,
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {isMobile && (
        <Drawer
          anchor="left"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: SIDEBAR_EXPANDED_WIDTH,
              height: "100%",
              borderRadius: 0,
              boxShadow: "none",
              borderRight: "1px solid rgba(148, 163, 184, 0.20)",
              boxSizing: "border-box",
              background: "transparent",
            },
          }}
          sx={{
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(2,6,23,0.38)",
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}
    </>
  );
}
